import { Injectable } from '@angular/core';
import { CurrencyPipe, PercentPipe } from '@angular/common';
import * as Parse from 'parse';

/**
 * Generates the values to be displayed in the public Analysis Tables.
 */
@Injectable()
export class PropertyAnalysisService {
    constructor(private dollarPipe: CurrencyPipe, private percentPipe: PercentPipe) {
    }

    private getPropertyAttribute(property: Parse.Object, column: string, attr: string) {
        let value = null;

        switch (property.get('type')) {
            case 'rental/flip':
                column === 'left' ?
                    value = property.get('saRental').get(attr)
                    :
                    value = property.get('saFlip').get(attr);
                break;
            case 'rental':
                value = property.get('saRental').get(attr);
                break;
            case 'flip':
                value = property.get('saFlip').get(attr);
                break;
        }

        return value;
    }

    getPurchasePrice(property: Parse.Object, column: string) {
        let value = this.getPropertyAttribute(property, column, 'purchasePrice');

        if (value && !Number.isNaN(Number(value))) {
            return this.dollarPipe.transform(value, 'USD', 'symbol', '1.0-0');
        }

        return 'N/A';
    }

    getARV(property: Parse.Object, column: string) {
        let value = this.getPropertyAttribute(property, column, 'arv');

        if (value && !Number.isNaN(Number(value))) {
            return this.dollarPipe.transform(value, 'USD', 'symbol', '1.0-0');
        }

        return 'N/A';
    }

    private getRehabAmountToDescriptor(amount: number): string {
        if (amount === 0) {
            return 'None';
        } else if (amount <= 20000) {
            return 'Light';
        } else if (amount <= 40000) {
            return 'Medium';
        } else {
            return 'Heavy';
        }

    }

    getRehabWord(property: Parse.Object, column: string) {
        let value = this.getPropertyAttribute(property, column, 'renoCost');

        if (value && !Number.isNaN(Number(value)) || value === 0) {
            return this.getRehabAmountToDescriptor(value);
        }

        return 'N/A';
    }

    getROI(property: Parse.Object, column?: string) {
        let value = null;

        switch (property.get('type')) {
            case 'rental/flip':
                column === 'left' ?
                    value = property.get('saRental').get('capRate')
                    :
                    value = property.get('saFlip').get('roi');
                break;
            case 'rental':
                column === 'left' ?
                    value = property.get('saRental').get('capRate')
                    :
                    value = property.get('saRental').get('leveragedCapRate');
                break;
            case 'flip':
                column === 'left' ?
                    value = property.get('saFlip').get('roi')
                    :
                    value = property.get('saFlip').get('cashOnCash');
                break;
        }

        if (value && !Number.isNaN(Number(value))) {
            return this.percentPipe.transform(value, '1.0-2');
        }

        return 'N/A';
    }

    private getRehabRangeLabel(amount: number): string {
        if (!amount || amount === 0) {
            return '';
        }

        const lowerValue = Math.round((amount * 0.8) / 1000);
        const upperValue = Math.round((amount * 1.2) / 1000);

        return '( ' + lowerValue + 'k - ' + upperValue + 'k )';
    }

    getRehabRange(property: Parse.Object, column: string) {
        let value = this.getPropertyAttribute(property, column, 'renoCost');

        if (value && !Number.isNaN(Number(value)) || value === 0) {
            return this.getRehabRangeLabel(value);
        }

        return 'N/A';
    }

    getNetIncome(property: Parse.Object, column?: string) {
        let value = null;

        switch (property.get('type')) {
            case 'rental/flip':
                column === 'left' ?
                    value = property.get('saRental').get('plusEquity')
                    :
                    value = property.get('saFlip').get('netIncomeCash');
                break;
            case 'rental':
                column === 'left' ?
                    value = property.get('saRental').get('cashFlow')
                    :
                    value = property.get('saRental').get('adjustedNetCashFlow');
                break;
            case 'flip':
                column === 'left' ?
                    value = property.get('saFlip').get('netIncomeCash')
                    :
                    value = property.get('saFlip').get('netIncomeFinanced');
                break;
        }

        if (value && !Number.isNaN(Number(value))) {
            return this.dollarPipe.transform(value, 'USD', 'symbol', '1.0-0');
        }

        return 'N/A';
    }
}
